import { Play, Pause, Lock, Gift, Download, MessageCircle, Star, Share2 } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useState } from "react";
import ClipShareModal from "@/components/ClipShareModal";
import EvidenceLockerReveal from "@/components/EvidenceLockerReveal";

const rotations = ["rotate-[-0.4deg]", "rotate-[0.5deg]", "rotate-[-0.7deg]", "rotate-[0.3deg]"];
const noteColors = ["bg-sticky", "bg-sticky_pink", "bg-paper"];

export default function EpisodeCard({ ep, idx, onUnlocked, isExpanded = false, onToggleExpand }) {
  const { current, playing, play, toggle } = usePlayer();
  const { user, shareStatus, redeemShare } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [clipOpen, setClipOpen] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const isCurrent = current?.number === ep.number;
  const showPause = isCurrent && playing;

  const handlePlay = () => {
    if (!ep.unlocked) return;
    if (isCurrent) toggle();
    else play(ep);
  };

  const handleDownload = async () => {
    if (!user) { toast.error("sign in to download"); nav("/login"); return; }
    if (!ep.unlocked) return;
    setDownloading(true);
    const t = toast.loading("preparing your watermarked copy...");
    try {
      const res = await api.get(`/episodes/${ep.number}/download`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `if-we-knew-ep${String(ep.number).padStart(2,"0")}.mp3`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("downloaded ♡ keep it personal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "download failed");
    } finally { toast.dismiss(t); setDownloading(false); }
  };

  // Pt 2 shares its parent's unlock; always pay/redeem against the parent number
  const unlockTarget = ep.parent_number || ep.number;

  const handleUnlock = async () => {
    if (!user) { toast.error("Sign up to unlock paid episodes"); nav("/signup"); return; }
    setLoading(true);
    setCheckoutError(null);
    try {
      const { data } = await api.post("/checkout/episode", {
        episode_number: unlockTarget,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not start checkout";
      toast.error(msg, { duration: Infinity, closeButton: true });
      setCheckoutError(msg);
      setLoading(false);
    }
  };

  const handleRedeemShare = async () => {
    setRedeeming(true);
    try {
      await redeemShare(unlockTarget);
      toast.success(`episode ${unlockTarget} unlocked — go press play ♡`);
      onUnlocked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem free unlock");
    } finally { setRedeeming(false); }
  };

  const rotation = rotations[idx % rotations.length];
  const noteColor = noteColors[ep.number % noteColors.length];
  const canRedeemShare = !ep.unlocked && !ep.is_free && shareStatus?.available;
  const expanded = isExpanded;

  // Toggle inline expand when clicking the card body (ignore clicks on buttons/links/inputs)
  const handleCardClick = (e) => {
    if (e.target.closest("button, a, input")) return;
    onToggleExpand?.();
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group relative bg-coal border ${expanded ? "border-hotpink" : "border-transparent"} p-4 md:p-6 flex gap-3 md:gap-5 items-start md:items-center transition-all ${rotation} ${!ep.unlocked ? "opacity-95" : ""} cursor-pointer`}
      data-testid={`episode-card-${ep.number}`}
    >
      {/* "Played" badge */}
      {ep.played && (
        <div
          className="absolute -top-2 -right-2 z-20 rotate-[8deg] bg-hotpink text-white font-outfit font-extrabold uppercase tracking-widest text-[9px] px-2 py-1 shadow-[2px_2px_0px_#FFFFFF]"
          data-testid={`played-badge-${ep.number}`}
          title="You've listened to this episode"
        >
          ♥ played
        </div>
      )}
      <div className="flex flex-col items-center justify-center min-w-[48px] md:min-w-[64px]">
        {ep.kind === "recap" ? (
          <>
            <span className="scribble text-hotpink text-xs uppercase tracking-widest">Bonus</span>
            <span className="brush-title text-3xl md:text-4xl text-hotpink leading-none">★</span>
          </>
        ) : (
          <>
            <span className="scribble text-white/40 text-xs uppercase tracking-widest">Ep</span>
            <span className="brush-title text-4xl md:text-6xl text-white leading-none" data-testid={`episode-number-${ep.number}`}>
              {String(ep.parent_number || ep.number).padStart(2, "0")}
            </span>
            {ep.part && (
              <span
                className="scribble text-hotpink text-base md:text-lg leading-none mt-1 rotate-[-3deg] inline-block"
                data-testid={`episode-part-${ep.number}`}
              >
                pt. {ep.part}
              </span>
            )}
          </>
        )}
      </div>

      <div className={`hidden md:flex flex-col items-center justify-center ${noteColor} text-ink p-3 w-32 h-24 shadow-md relative ${idx % 2 ? "rotate-[-4deg]" : "rotate-[3deg]"}`}>
        <div className="tape" />
        <span className="scribble text-base leading-tight text-center">{ep.recap_note}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <Link to={`/episodes/${ep.number}`} data-testid={`open-episode-${ep.number}`} className="font-outfit font-bold text-xl md:text-2xl text-white hover:text-hotpink transition-colors">
            {ep.title}
          </Link>
          {ep.is_free && (
            <span className="scribble text-hotpink text-lg" data-testid={`free-tag-${ep.number}`}>· free ·</span>
          )}
          {!ep.is_free && ep.unlocked && (
            <span className="scribble text-green-400 text-lg">· unlocked ·</span>
          )}
        </div>
        <p
          className={`text-white/70 text-sm md:text-base font-outfit ${expanded ? "" : "line-clamp-2"}`}
          data-testid={`episode-description-${ep.number}`}
        >
          {ep.description}
        </p>
        {expanded && ep.recap_note && (
          <p className="scribble text-white/85 text-lg mt-3 leading-snug rotate-[-1deg] inline-block" data-testid={`episode-recap-full-${ep.number}`}>
            &ldquo;{ep.recap_note}&rdquo;
          </p>
        )}
        {expanded && ep.played && ep.evidence_images && (
          <EvidenceLockerReveal ep={ep} images={ep.evidence_images} />
        )}
        {expanded && ep.unlocked && ep.status !== "coming_soon" && (
          <div className="mt-3">
            <button
              onClick={() => setClipOpen(true)}
              data-testid={`share-clip-btn-${ep.number}`}
              className="inline-flex items-center gap-2 text-white/60 hover:text-hotpink font-outfit text-xs uppercase tracking-widest font-bold transition-colors"
            >
              <Share2 size={12} /> share a 30s clip
            </button>
          </div>
        )}
        <div className="flex items-center gap-4 mt-2 text-white/40 text-xs uppercase tracking-widest font-outfit">
          <span>{ep.duration_label} · audio</span>
          {ep.rating?.count > 0 ? (
            <span className="flex items-center gap-1 text-hotpink" data-testid={`rating-${ep.number}`}>
              <Star size={12} fill="currentColor"/> {ep.rating.avg.toFixed(1)} <span className="text-white/40 normal-case">({ep.rating.count})</span>
            </span>
          ) : (
            <Link to={`/episodes/${ep.number}`} className="text-white/40 hover:text-hotpink flex items-center gap-1">
              <Star size={12}/> rate
            </Link>
          )}
          <Link to={`/episodes/${ep.number}`} data-testid={`comments-link-${ep.number}`} className="text-white/40 hover:text-hotpink flex items-center gap-1">
            <MessageCircle size={12}/> {ep.comments_count || 0}
          </Link>
        </div>
      </div>

      <div className="flex-shrink-0 flex flex-col gap-2 items-end">
        {ep.status === "coming_soon" ? (
          <div
            className="flex flex-col items-center justify-center min-w-[110px] h-16 bg-coal border border-hotpink/40 text-hotpink font-outfit font-extrabold px-3 text-center"
            data-testid={`coming-soon-${ep.number}`}
          >
            <span className="text-[10px] uppercase tracking-widest">coming</span>
            <span className="text-sm">soon</span>
            {ep.release_date && (
              <span className="text-[9px] uppercase tracking-widest text-white/60 mt-1" data-testid={`coming-soon-date-${ep.number}`}>
                {new Date(ep.release_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        ) : ep.unlocked ? (
          <>
            <button
              onClick={handlePlay}
              data-testid={`play-episode-${ep.number}`}
              aria-label={`Play episode ${ep.number}`}
              className="w-12 h-12 md:w-16 md:h-16 bg-hotpink hover:bg-hotpink_dark text-white flex items-center justify-center shadow-[3px_3px_0px_#FFFFFF] md:shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_#FFFFFF] transition-all"
            >
              {showPause ? <Pause size={20} fill="white"/> : <Play size={20} fill="white"/>}
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading}
              data-testid={`download-episode-${ep.number}`}
              title="Download a personally watermarked copy"
              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-white/70 hover:text-hotpink border border-white/20 hover:border-hotpink px-3 py-1 transition-colors disabled:opacity-60"
            >
              <Download size={11}/> {downloading ? "..." : "download"}
            </button>
          </>
        ) : ep.kind === "recap" ? (
          <div className="flex flex-col items-center justify-center min-w-[110px] h-16 bg-coal border border-hotpink/40 text-hotpink font-outfit font-extrabold px-3 text-center" data-testid={`recap-locked-${ep.number}`}>
            <span className="text-[10px] uppercase tracking-widest">finish</span>
            <span className="text-sm">Ep {ep.gate_episode} first</span>
          </div>
        ) : (
          <>
            <button
              onClick={handleUnlock}
              disabled={loading}
              data-testid={`unlock-episode-${ep.number}`}
              className="flex flex-col items-center justify-center min-w-[100px] h-16 bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold transition-all px-4 shadow-[4px_4px_0px_#FF007F] hover:shadow-[6px_6px_0px_#FFFFFF] disabled:opacity-60"
            >
              <span className="flex items-center gap-2">
                <Lock size={14}/> <span className="text-lg">$1</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest">{loading ? "..." : "unlock"}</span>
            </button>
            {ep.part && (
              <span className="text-[9px] uppercase tracking-widest text-white/40 mt-1 text-center max-w-[110px] leading-tight" data-testid={`unlock-both-parts-hint-${ep.number}`}>
                unlocks both parts
              </span>
            )}
            {checkoutError && (
              <div
                className="mt-1 max-w-[220px] bg-red-900/40 border border-red-500/50 text-red-100 text-[10px] font-outfit p-2 leading-tight"
                data-testid={`checkout-error-${ep.number}`}
              >
                <div className="font-bold uppercase tracking-widest text-red-300 text-[9px] mb-0.5">Checkout error</div>
                {checkoutError}
              </div>
            )}
            {canRedeemShare && (
              <button
                onClick={handleRedeemShare}
                disabled={redeeming}
                data-testid={`free-unlock-${ep.number}`}
                className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-hotpink hover:text-white border border-hotpink hover:bg-hotpink px-3 py-1 transition-colors disabled:opacity-60"
                title="Use your free share unlock on this episode"
              >
                <Gift size={11}/> {redeeming ? "..." : "use free unlock"}
              </button>
            )}
          </>
        )}
      </div>
      <ClipShareModal ep={ep} open={clipOpen} onClose={() => setClipOpen(false)} />
    </div>
  );
}
